// /Users/stuartmcgeer/portalworx/frontend/src/contexts/AuthContext.tsx

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axiosInstance from '../services/axiosInstance';

// Define the shape of the AuthContext
interface AuthContextType {
    username: string;
    first_name: string;
    last_name: string;
    loading: boolean;
    error: string | null;
    isAuthenticated: boolean;
    tenant: string; // Add tenant property
    setUsername: React.Dispatch<React.SetStateAction<string>>;
    setFirstName: React.Dispatch<React.SetStateAction<string>>;
    setLastName: React.Dispatch<React.SetStateAction<string>>;
    setTenant: React.Dispatch<React.SetStateAction<string>>; // Add setTenant method
    logout: () => void;
    fetchUserData: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [username, setUsername] = useState<string>('');
    const [first_name, setFirstName] = useState<string>('');
    const [last_name, setLastName] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [tenant, setTenant] = useState<string>(''); // Add tenant state

    const fetchUserData = async () => {
        const token = localStorage.getItem('token');
        if (token) {
            setLoading(true);
            try {
                const response = await axiosInstance.get('user/');
                setUsername(response.data.username);
                setFirstName(response.data.first_name);
                setLastName(response.data.last_name);
    
                // Dynamically set tenant
                const tenantFromHost = window.location.host.split('.')[0];
                setTenant(tenantFromHost);
                localStorage.setItem('tenant', tenantFromHost);
            } catch (error) {
                console.error('Error fetching user details:', error);
                setError('Failed to load user details. Please log in again.');
                localStorage.removeItem('token');
            } finally {
                setLoading(false);
            }
        } else {
            setLoading(false);
        }
    };
    

    useEffect(() => {
        fetchUserData();
    }, []);

    const isAuthenticated = !!username;

    const logout = () => {
        setUsername('');
        setFirstName('');
        setLastName('');
        setTenant('');
        localStorage.removeItem('token');
    };

    return (
        <AuthContext.Provider
            value={{
                username,
                first_name,
                last_name,
                loading,
                error,
                isAuthenticated,
                tenant, // Provide tenant value
                setUsername,
                setFirstName,
                setLastName,
                setTenant, // Provide setTenant method
                logout,
                fetchUserData,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

